/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { GlobalConstants } from '../../constants/global-constants';
import { Observable, Subject, of, switchMap, take, takeUntil } from 'rxjs';
import { TabDescription } from 'libs/sysque/toolbelt/data-access/src/lib/domain/tab-description';
import { Item } from 'libs/sysque/toolbelt/data-access/src/lib/domain/item';
import { TooltipItem } from '../../../../../data-access/src/lib/domain/tooltip-item';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import { SystemInfo } from '../../../../../data-access/src/lib/domain/system-info';
import {
  IManageModePanelFrameFacade,
  MANAGE_MODE_PANEL_FRAME_FACADE_TOKEN,
} from '../../../../../data-access/src/lib/domain/facades-models/manage-mode-panel-frame-facade-interface';
import { DxContextMenuComponent } from 'devextreme-angular/ui/context-menu';
import { PastedItemsInfo } from '../../models/pasted-items-info';
import { SystemCategories } from '../../models/system-categories';
import { CategoryScopeComponent } from '../category-scope/category-scope.component';
import { SysQueStateService } from '../../../../../utils/src/lib/sysque-electrical-state/sysque-elecrical-state.service';
import { SysQueCopyPasteService } from '../../../../../utils/src/lib/sysque-copy-paste/sysque-copy-paste.service';

@Component({
  selector: 'sysque-manage-mode-dest-panel-frame',
  templateUrl: './manage-mode-dest-panel-frame.component.html',
  styleUrls: ['./manage-mode-dest-panel-frame.component.scss'],
  standalone: false
})
export class ManageModeDestPanelFrameComponent implements OnInit, OnDestroy {
  @Input() showAddFittingBtn: boolean;
  @Input() isSystemSource: boolean;
  @Input() key: string;
  @Input() showRootPath: boolean;
  @Output() pasteError: EventEmitter<string> = new EventEmitter<string>();
  @Output() showDuplicateError: EventEmitter<PastedItemsInfo[]> = new EventEmitter<PastedItemsInfo[]>();
  public sourceState: SysqueElectricalState = new SysqueElectricalState();
  public categoryList$: Observable<TabDescription[]>;
  public fittingList$: Observable<Item[]>;
  public systemInfo$: Observable<SystemInfo[]>;
  public state: SysqueElectricalState = new SysqueElectricalState();
  public system: string = '';
  public isContextMenuVisible: boolean = this.system != '';
  public destinationMenuItems: any[] = [{ text: 'Paste' }];
  public fittingList: Item[];
  public tooltipItems: TooltipItem[] = [];
  public defaultCategory: string;
  public stateSystemInfo$: Observable<SystemInfo[]>;
  public stateCategoryList$: Observable<TabDescription[]>;
  public stateFittingsList$: Observable<Item[]>;
  public warningMsg: string = '';
  public copiedItemInfo: PastedItemsInfo = new PastedItemsInfo();
  public copiedItemList: PastedItemsInfo[] = [];
  public categories = SystemCategories.getSystemCategories().slice(2, 7);
  public IsCopyCategory = false;
  public isHangerSetUp = false;
  private readonly closed$ = new Subject<void>();
  public copiedItems: Item[] = [];
  public duplicateItemLists: PastedItemsInfo[] = [];
 

  @ViewChild(CategoryScopeComponent) categoryScope: CategoryScopeComponent;

  constructor(
    @Inject(MANAGE_MODE_PANEL_FRAME_FACADE_TOKEN) public facade: IManageModePanelFrameFacade,
    private cd: ChangeDetectorRef,
    public sysqueStateService: SysQueStateService,
    public copyPasteService: SysQueCopyPasteService
  ) {}

  public ngOnInit(): void {
    this.sysqueStateService.setManageDestState(this.sysqueStateService.getElectricalState());
    this.sysqueStateService.manageDest$.pipe(takeUntil(this.closed$)).subscribe((state) => {
      if(localStorage.getItem(GlobalConstants.MANAGE_MODE_WINDOW)) {
        this.state = state;
        this.updateData();
      }
     });
    this.IsCopyCategory = false;
    this.clearFittingsAndCategories();
    this.updateData();
    // this.facade.setManageModeDestinationState(this.state);
    this.retrieveCategories(this.state.selectedSystem);
    this.setDefaultCategoryAndFittingList();
  }

  private updateData(): void {
    this.systemInfo$ = of(this.state.systemList);
    this.system = this.state.selectedSystem;
    this.defaultCategory = this.state.selectedCategory;
    this.categoryList$ = of(this.state.categoryList);
    this.fittingList$ = of(this.state.fittingList);
    const copiedItemList = this.copyPasteService.getCopiedItemsList();
    if (Array.isArray(this.state.fittingList)) {
    this.state.fittingList.forEach((fitting) => {
      if (copiedItemList.includes(fitting)) {
        fitting.isCopyState = false;
      }
    });
  }
    this.retrieveCategories(this.state.selectedSystem);
    this.fittingList = this.state.fittingList;
    this.defaultCategory = this.state.selectedCategory;
    this.state.selectedSystem = this.system;
    this.cd.markForCheck();
  }

  public ngOnDestroy(): void {
    this.closed$.next();
    this.closed$.complete();
  }

  @ViewChildren(DxContextMenuComponent) contextMenus: QueryList<DxContextMenuComponent>;
  public onContextMenuShowing(event): void {
    this.contextMenus.forEach((contextMenu) => {
      if (contextMenu !== event.component) {
        contextMenu.visible = false;
      }
    });
  }

  public onContextMenuItemClick(event): void {
    if (event.itemData.text === 'Paste') {
      this.warningMsg = '';
      this.IsCopyCategory = false;
      this.copiedItemInfo = new PastedItemsInfo();
      this.sourceState = this.sysqueStateService.getManageSrcState();
      if (this.validatePasteScenario(this.state, this.sourceState) === false) {
        this.pasteError.emit(this.warningMsg);
      } else {
        this.checkForDuplicateItems();
      }
    }
  }

  public removeItem(item: Item): void {
    this.resetItemStyle(item, true);
    let srcCopied: Item[] = [];
    this.copyPasteService.copiedItemsList$.pipe(takeUntil(this.closed$)).subscribe((data) => {
      srcCopied = data;
    });
    const srcCopiedIndex = srcCopied.findIndex((i) => i.name === item.name);
    if (srcCopiedIndex !== -1) {
      srcCopied.splice(srcCopiedIndex, 1);
    }
    this.copyPasteService.setCopiedItemsList(srcCopied);
    this.facade.updateLocalStorage(GlobalConstants.DuplicateItemsUpdate, true);
  }

  public onPasteEventLogic(copiedItems: PastedItemsInfo[]): void {
    this.facade.executePasteItemsAction(copiedItems);
    this.facade.updateLocalStorage(GlobalConstants.CanExecutePasteLogic, false);
    this.resetItemStyle(null, false);
    this.updateStatus();
  }

  public executeCopyCategoryLogic(isReplaceAll: boolean): void {    
    this.facade.logicToExecuteForCopyCategory(isReplaceAll);
    this.updateStatus();
  }

  public updateStatus(): void {
    this.systemInfo$ = of(this.state.systemList);
    this.categoryList$ = of(this.state.categoryList);
    this.fittingList$ = this.facade.getFittings(this.state.selectedSystem, this.state.selectedCategory);
    this.fittingList$.pipe(takeUntil(this.closed$)).subscribe((response) => {
      this.fittingList = response;
      this.sysqueStateService.updateDestProperty('fittingList', this.fittingList);
    });
   
    this.cd.markForCheck();
  }

  public resetItemStyle(item: Item, isCancel: boolean): void {
    this.categoryScope.resetStyle(item, isCancel);
    this.copyPasteService.setCopiedItemsName([]);
    this.copyPasteService.setCopiedItemsList([]);
    this.copyPasteService.setDuplicateItemsList([]);
    this.copyPasteService.setUniqueCopiedItemsList([]);
  }

  public validatePasteScenario(destState: SysqueElectricalState, sourceState: SysqueElectricalState): boolean {
    const ifDestCategoryIsCustomContent = this.checkIfCustomContent(destState);
    const ifSrcCategoryIsCustomContent = this.checkIfCustomContent(sourceState);

    if (destState.selectedSystem == sourceState.selectedSystem) {
      this.IsCopyCategory
        ? (this.warningMsg = GlobalConstants.CopyCategorySameSystem)
        : (this.warningMsg = GlobalConstants.BothSelectedSystemSame);
      return false;
    }
    if (ifSrcCategoryIsCustomContent != 'true') {
      if (sourceState.selectedCategory != destState.selectedCategory) {
        this.IsCopyCategory
          ? (this.warningMsg =
              GlobalConstants.CopyCategoryMismatched +
              sourceState.selectedCategory +
              GlobalConstants.CopyCategoryMismatchedPart)
          : (this.warningMsg =
              GlobalConstants.CategoryMismatched +
              sourceState.selectedCategory +
              GlobalConstants.CategoryMismatchedPart +
              sourceState.selectedCategory +
              GlobalConstants.CategoryMismatchedPart2);
        return false;
      }
    } else {
      if (ifDestCategoryIsCustomContent != 'true') {
        this.IsCopyCategory
          ? (this.warningMsg =
              GlobalConstants.CopyCategoryMismatched +
              sourceState.selectedCategory +
              GlobalConstants.CopyCategoryMismatchedPart)
          : (this.warningMsg = GlobalConstants.CustomContentFitting);
        return false;
      }
    }

    this.getSelectedItemsDetails();
    const result = this.categories.some((category) => category.text == this.copiedItemInfo.category);
    if (result) {
      const isSrcClearance = sourceState.systemList.find((x) => x.name === sourceState.selectedSystem)?.isClearance;
      const isDestClearance = destState.systemList.find((x) => x.name === destState.selectedSystem)?.isClearance;
      const response = isSrcClearance === isDestClearance;
      if (!response) {
        this.warningMsg = GlobalConstants.ClearanceSystemTypeMisMatch;
        return false;
      }
    }
    return true;
  }

  public checkIfCustomContent(state: SysqueElectricalState): string {
    const selectedCategory = state.categoryList.find((category) => category.tabName === state.selectedCategory);
    return selectedCategory ? selectedCategory.isCustomContent.toLowerCase() : '';
  }

  public getSelectedItemsDetails(): void {
    let copiedItems: Item[] = [];
    this.copyPasteService.copiedItemsList$.subscribe((data) => {
      copiedItems = data;
    });
    const list = this.IsCopyCategory ? this.sourceState.fittingList : copiedItems;
    this.copiedItemList = [];
    Object.values(list).forEach((item) => {
      this.copiedItemInfo = new PastedItemsInfo();
      this.copiedItemInfo.copiedItem = item;
      this.copiedItemInfo.srcSystemName = this.sourceState.selectedSystem;
      this.copiedItemInfo.destSystemName = this.state.selectedSystem;
      this.copiedItemInfo.category = this.sourceState.selectedCategory;
      this.copiedItemInfo.destCategory = this.state.selectedCategory;
      this.copiedItemInfo.isDestCustomContent = this.state.categoryList.filter(
        (category) => category.tabName == this.state.selectedCategory
      )[0]?.isCustomContent;
      this.copiedItemList.push(this.copiedItemInfo);
    });
  }

  public async checkForDuplicateItems(): Promise<void> {
    let filteredList: PastedItemsInfo[] = [];
    let uniqueItemList: PastedItemsInfo[] = [];    
    this.duplicateItemLists = await this.facade.checkForDuplicateItemsInDestination(this.copiedItemList);
    if (this.duplicateItemLists.length > 0) {
      filteredList = this.duplicateItemLists?.filter((item) => item.isDuplicate);
      uniqueItemList = this.duplicateItemLists?.filter((item) => item.isDuplicate === false);
      this.copyPasteService.setUniqueCopiedItemsList(uniqueItemList);
    }

    if (filteredList.length > 0) {
      this.warningMsg = "'" + this.sourceState.fittingList[0].name + GlobalConstants.DoReplaceExistingItem;
      this.IsCopyCategory
        ? this.facade.updateLocalStorage(GlobalConstants.CopyCategory, {
            status: 'true',
            destSystem: this.state.selectedSystem,
          })
        : this.showDuplicateError.emit(filteredList);
    } else {
      this.onPasteEventLogic(this.copiedItemList);
    }
  }

  public onRightClick(e: MouseEvent): void {
    this.copiedItems = [];
    this.copyPasteService.copiedItemsList$.pipe(takeUntil(this.closed$)).subscribe((data) => {
      this.copiedItems.push(...data);
    });
    e.preventDefault();
  }

  public onValueChanged(e: any): void {
    if (e.value === undefined || e.event === undefined) return;
    this.clearFittingsAndCategories();
    this.categoryList$ = of([]);
    this.state.selectedSystem = e.value;
    this.sysqueStateService.updateDestProperty('selectedSystem', e.value);

    this.initializeSystem(e.value);
    this.systemInfo$ = of(this.state.systemList);

    this.facade.updateLocalStorage(GlobalConstants.MANAGE_MODE_DESTINATION_SYSTEM_STATE, this.state);

    this.retrieveCategories(e.value as string);
    this.setDefaultCategoryAndFittingList();
  }

  public clearFittingsAndCategories(): void {
    this.fittingList$ = of([]);
    this.fittingList = [];
  }

  private initializeSystem(selectedSystem: any): void {
    this.showAddFittingBtn = true;
    this.system = selectedSystem;
    this.state.selectedSystem = selectedSystem;
    this.updateState();
  }

  private retrieveCategories(selectedSystem: string): void {
    this.categoryList$ = this.facade.notifyGetCategoryListAction(selectedSystem);
    this.categoryList$.pipe(take(1)).subscribe((categories) => {
      this.sysqueStateService.updateProperty('categoryList', categories);
      this.state.categoryList = categories;
    });
  }

  public setDefaultCategoryAndFittingList(): void {
    this.categoryList$
      .pipe(
        take(1),
        switchMap((categories) => {
          if (categories && categories.length > 0) {
            this.defaultCategory = categories[0].tabName;
            this.sysqueStateService.updateDestProperty('selectedCategory', this.defaultCategory);
          }

          return this.facade.getFittings(this.state.selectedSystem, this.defaultCategory);
        }),
        take(1)
      )
      .subscribe((data) => {
        if(!data){
          this.sysqueStateService.updateDestProperty('fittingList', []);
          this.fittingList = [];
          this.updateState();
        }
        else{
          const fittingList = data.map((item) => {
            return {
              ...item,
              tooltipItems: this.facade.getTooltipItemInfo(item, this.defaultCategory),
            };
          });
          this.sysqueStateService.updateDestProperty('fittingList', fittingList);
          this.fittingList = fittingList;
          this.updateState();
      }
      });
  }

  public onCategoryChanged(e: any): void {
    if (e.value === undefined || e.event === undefined) return;
    this.clearFittingsAndCategories();

    this.fittingList$ = of([]);
    this.fittingList = [];
    this.fittingList$ = this.facade.notifyGetFittingsListAction(this.state.selectedSystem, e.value);
    this.fittingList$.pipe(takeUntil(this.closed$)).subscribe((response) => {
      this.state.fittingList = response;
      this.sysqueStateService.updateDestProperty('fittingList', response);
      this.fittingList = response;
      this.sysqueStateService.updateDestProperty('selectedCategory', e.value);
    });
    this.state.selectedCategory = e.value;
    this.defaultCategory = e.value;
    this.updateState();
  }

  //#endregion

  public updateState(): void {
    this.sysqueStateService.setManageDestState(this.state);
    this.facade.updateLocalStorage(GlobalConstants.MANAGE_MODE_DESTINATION_SYSTEM_STATE, this.state);
   }

  public validateCopyCategory(): void {
    this.sourceState = this.sysqueStateService.getManageSrcState();
    this.IsCopyCategory = true;

    if (!this.validatePasteScenario(this.state, this.sourceState)) {
      this.pasteError.emit(this.warningMsg);
      return;
    }

    if (this.copiedItemInfo.category.toLowerCase() !== GlobalConstants.BuildingLayout.toLowerCase()) {
      this.checkForDuplicateItemsForCopyCategory();
      return;
    }  
    
    const result = this.state.isHangerNeeded;
    if (result) {
      this.facade.updateLocalStorage(GlobalConstants.ShowHangerSetUp, 'true');
      return;
    }
    else {
      this.checkForDuplicateItemsForCopyCategory();
    }
  }

  public checkForDuplicateItemsForCopyCategory(): void {
    this.facade
      .checkForDuplicateItemsInDestForCopyCategory(this.copiedItemList)
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.facade.updateLocalStorage(GlobalConstants.CopyCategory, {
            status: 'true',
            destSystem: this.state.selectedSystem,
          });
        } else {
          this.executeCopyCategoryLogic(result);
        }
      });
      
  }  
}